import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';

import AppInput from '../../UI/AppInput';
import Button from '../../UI/Button';
import Checkbox from '../../UI/Checkbox';

import { PASSPORT_NUMBER, TEXT } from '../../app_constants';
import bemClassName from '../../utils/bem';

import './index.scss';
import { RootState } from '../../store';

import PrivateData from '../../API/PrivateData';
import Validate from '../../API/Validate';
import useDebounce from '../../utils/debounce';

const passportData = bemClassName('passport-data');
const personalData = bemClassName('personal-data');

const PassportData = () => {
  const [userPassportData, setUserPassportData] = useState({});

  const [surname, setSurname] = useState<string>();
  const [name, setName] = useState<string>();
  const [middlename, setMiddlename] = useState<string>();

  const [passportNumber, setPassportNumber] = useState<string>('');
  const [passportErrorText, setPassportErrorText] = useState<string>();
  const [checkbox, setCheckBox] = useState<boolean>();
  const [passportError, setPassportError] = useState<string>();

  const [errorBoolean, setErrorBoolean] = useState<boolean>(false);
  const [disabledButton, setDisabledButton] = useState<boolean>(false);

  const [userPassportDataEdit, setUserPassportDataEdit] = useState<boolean>(false);

  const [userPassportApproved, setUserPassportApproved] = useState<boolean>(false);

  // const [passportIsValid, setpassportisValid] = useState<boolean>(false);

  const [passportIsValidNumber, setpassportisValidNumber] = useState<boolean>();

  const isLogin = useSelector((state: RootState) => state.autorizationStatus.isLogin);

  const debouncedName = useDebounce(name, 500);
  const debouncedSurname = useDebounce(surname, 500);
  const debouncedPassportNumber = useDebounce(passportNumber, 500);
  const navigate = useNavigate();

  const handleSetSurname = (value: string) => {
    setSurname(value);
  };

  const handleSetName = (value: string) => {
    setName(value);
  };

  const handleSetMiddlename = (value: string) => {
    setMiddlename(value);
  };

  const handleSavePassportData = () => {
    if (userPassportData) {
      setDisabledButton(false);
      PrivateData.getPrivateEditUserDate(userPassportData).then(() => {
        setDisabledButton(true);
        setUserPassportDataEdit(true);
        if (!userPassportApproved) {
          navigate('#proof-of-identity');
        }
      });
    }
  };

  const handleSetPassportNumber = (number: string) => {
    setPassportNumber(number);

    if (number === '') {
      // setpassportisValid(false);
    }
  };

  const handleSetCheckBox = () => {
    if (!checkbox) {
      setCheckBox(true);
    } else {
      setCheckBox(false);
    }
  };

  useEffect(() => {
    setErrorBoolean(Boolean(passportNumber));

    // setDisabledButton(!!(passportNumber && passportCode && passportDate && passportDivision));

    setDisabledButton(!!(passportNumber && name && surname && middlename && checkbox));

    if (passportNumber && name && surname && middlename) {
      setUserPassportData({
        passport: {
          number: passportNumber ?? undefined,
          approved: false,
        },
        name,
        surname,
        middlename,
      });
      setUserPassportApproved(false);
    }
  }, [passportNumber, name, surname, middlename, checkbox]);

  useEffect(() => {
    if (isLogin !== null && isLogin) {
      PrivateData.getPrivateUser().then((item) => {
        setName(item.name);
        setMiddlename(item.middlename);
        setSurname(item.surname);
        if (item.passport.approved === true) {
          setUserPassportApproved(true);
        }
        if (item.passport.issued !== null || item.passport.number !== null) {
          if (item.passport.error) {
            setPassportError(item.passport.error);
          }
          setPassportNumber(item.passport.number);
          if (item.passport.issued !== null && item.passport.number) {
            setUserPassportDataEdit(true);
          }
        }
      });
    }
  }, [isLogin]);

  useEffect(() => {
    const abortControllerPassportNumber = new AbortController();
    if (debouncedPassportNumber) {
      setpassportisValidNumber(true);
      (async () => {
        try {
          const resp = await Validate.checkPassport(
            debouncedPassportNumber,
            abortControllerPassportNumber.signal,
          );
          if (resp.series && resp.number) {
            setPassportNumber(`${resp.series} ${resp.number}`);
            // setpassportisValid(true);
          }
          if (resp.warnings && resp.warnings[0]) {
            setPassportErrorText(resp.warnings[0]);
            setpassportisValidNumber(false);
          }
        } catch (error) {
          if (typeof error === 'object' && error !== null && error instanceof Error) {
            if (error.name !== 'AbortError') {
              if (error instanceof AxiosError && error.response && error.response.data) {
                setPassportErrorText(error.response.data);
              } else {
                setPassportErrorText('Неверный номер паспорта');
              }
              if (passportNumber) {
                setpassportisValidNumber(false);
              }
            }
          }
        }
      })();
    }
    return () => {
      abortControllerPassportNumber.abort();
    };
  }, [debouncedPassportNumber]);

  useEffect(() => {
    if (debouncedName) {
      (async () => {
        try {
          const responseData = await Validate.helperName(debouncedName);
          if (responseData[0]) {
            // setDataListName(responseData);
            if (responseData.length === 1) {
              setName(responseData[0]);
            }
          }
        } catch (error) {
          const err = error as Error | AxiosError;
          if (err.name === 'AbortError') {
            // return error;
          }
        }
      })();
    }
  }, [debouncedName]);

  useEffect(() => {
    if (debouncedSurname) {
      (async () => {
        try {
          const responseData = await Validate.helperSurname(debouncedSurname);
          if (responseData[0]) {
            // setDataListSurname(responseData);
            if (responseData.length === 1) {
              setSurname(responseData[0]);
            }
          }
        } catch (error) {
          const err = error as Error | AxiosError;
          if (err.name === 'AbortError') {
            // return error;
          }
        }
      })();
    }
  }, [debouncedSurname]);

  useEffect(() => {
    document.title = 'Паспортные данные';
  }, []);

  return !userPassportDataEdit || passportError ? (
    <>
      <h2>Верификация: паспортные данные</h2>
      <AppInput
        inputId="surname"
        text="Фамилия по паспорту"
        type={TEXT}
        externalClassName={personalData('field')}
        required
        handleExternal={handleSetSurname}
        validate="Необходимо указать Фамилию, Имя и Отчество"
        valueExternal={surname}
        valueBoolean={errorBoolean}
      />
      <AppInput
        inputId="name"
        text="Имя по паспорту"
        type={TEXT}
        externalClassName={personalData('field')}
        required
        handleExternal={handleSetName}
        validate="Необходимо указать Фамилию, Имя и Отчество"
        valueExternal={name}
        valueBoolean={errorBoolean}
      />
      <AppInput
        inputId="middlename"
        text="Отчество по паспорту"
        type={TEXT}
        externalClassName={personalData('field')}
        handleExternal={handleSetMiddlename}
        valueExternal={middlename}
      />
      <AppInput
        inputId="passportNumber"
        text="Номер и серия паспорта "
        type={TEXT}
        required
        externalClassName={passportData('field')}
        placeholder="Введите номер и серию паспорта"
        handleExternal={handleSetPassportNumber}
        validate={passportErrorText ?? ''}
        isValidExternal={passportIsValidNumber}
        dataType={PASSPORT_NUMBER}
        valueExternal={passportNumber ?? undefined}
        valueBoolean={errorBoolean}
      />
      <Checkbox
        text="Я подтверждаю, что ознакомился"
        linkText=" с условиями публичной оферты"
        linkUrl="/account#service-agreement"
        externalClassName={passportData('checkbox')}
        handleExternal={handleSetCheckBox}
      />
      <Button
        handleExternal={handleSavePassportData}
        text="Сохранить"
        disable={!disabledButton}
        externalClassName={passportData('button')}
      />
    </>
  ) : (
    <div className={passportData('user-data')}>
      <div className={passportData('user')}>
        <div className={passportData('passport-info')}>
          <p className={passportData('placeholder-text')}>ФИО</p>
          <p className={passportData('passport-data')}>
            {surname} {name} {middlename}
          </p>
        </div>
        <div className={passportData('passport-info')}>
          <p className={passportData('placeholder-text')}>Номер и серия паспорта</p>
          <p className={passportData('passport-data')}>{passportNumber}</p>
        </div>
        <button
          className={passportData('edit-button')}
          onClick={() => setUserPassportDataEdit(false)}
          type="button">
          Редактировать
        </button>
      </div>
    </div>
  );
};

export default PassportData;
